/**
 * 대시보드 상태 판정 조건 유틸리티
 * 
 * 프로젝트/견적/쪽지함의 상태를 판정하는 로직을 한 곳에 모아 유지보수성을 높입니다.
 * 설계 문서: docs/dashboard/DASHBOARD_DESIGN.md
 */

// ============================================================================
// 프로젝트 상태 판정
// ============================================================================

/**
 * 프로젝트 상태 분류
 * - requesting: 요청중 (견적 수집 중, 견적 0개)
 * - reviewing: 검토중 (견적 도착, 아직 결정 안 함)
 * - in_progress: 진행중 (견적 수락 완료)
 */
export type ProjectStatusCategory = 'requesting' | 'reviewing' | 'in_progress'

export interface ProjectStatusContext {
  counselStatus: string | null  // counsel.counsel_status
  estimateCount: number         // 견적 개수
  hasAcceptedEstimate: boolean  // 수락된 견적이 있는지
  dueDate?: string | null       // ISO 8601
}

/**
 * 프로젝트 상태 카테고리 판정
 */
export function getProjectStatusCategory(context: ProjectStatusContext): ProjectStatusCategory {
  // 진행중: 수락된 견적이 있으면 진행중
  if (context.hasAcceptedEstimate) {
    return 'in_progress'
  }

  // 검토중: 견적이 1개 이상 있으면 검토중
  if (context.estimateCount > 0) {
    return 'reviewing'
  }

  // 요청중: 견적이 없으면 요청중
  return 'requesting'
}

/**
 * 마감 임박 여부 판정 (D-3 이내)
 */
export function isDeadlineApproaching(dueDate?: string | null): boolean {
  if (!dueDate) return false

  const due = parseDateInLocalTimezone(dueDate)
  if (!due) return false

  due.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  return diffDays >= 0 && diffDays <= 3
}

/**
 * DATE 타입 문자열(YYYY-MM-DD) 또는 ISO 문자열을
 * 로컬 타임존 기준 날짜로 파싱
 */
function parseDateInLocalTimezone(dateValue: string): Date | null {
  const dateString = dateValue.split('T')[0]
  const dateParts = dateString.split('-')

  if (dateParts.length !== 3) return null

  const year = parseInt(dateParts[0], 10)
  const month = parseInt(dateParts[1], 10) - 1
  const day = parseInt(dateParts[2], 10)

  if (isNaN(year) || isNaN(month) || isNaN(day)) return null

  const date = new Date(year, month, day)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * D-day 계산
 * DATE 타입 문자열(YYYY-MM-DD)을 로컬 타임존 기준으로 올바르게 파싱
 */
export function calculateDaysUntilDue(dueDate?: string | null): number | null {
  if (!dueDate) return null

  try {
    const due = parseDateInLocalTimezone(dueDate)
    if (!due) return null

    due.setHours(0, 0, 0, 0)
    
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    const diffTime = due.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    return diffDays
  } catch (error) {
    return null
  }
}

// ============================================================================
// 견적 상태 판정
// ============================================================================

/**
 * 견적 미열람 여부 판정
 * - estimate_views 테이블에 해당 견적의 열람 기록이 없으면 미열람
 */
export function isEstimateUnread(estimateId: number, viewedEstimateIds: number[]): boolean {
  return !viewedEstimateIds.includes(estimateId)
}

/**
 * 견적 신규 여부 판정 (24시간 이내 제출)
 */
export function isEstimateNew(submittedAt: string): boolean {
  const submitted = new Date(submittedAt)
  const now = new Date()
  const diffHours = (now.getTime() - submitted.getTime()) / (1000 * 60 * 60)
  return diffHours <= 24
}

/**
 * 결정 필요 프로젝트 판정
 * - 견적이 1개 이상 있고, 아직 수락하지 않은 프로젝트
 */
export function needsDecision(context: ProjectStatusContext): boolean {
  return context.estimateCount > 0 && !context.hasAcceptedEstimate
}

// ============================================================================
// 쪽지함 상태 판정
// ============================================================================

/**
 * 쪽지 미열람 여부 판정
 * - notifications.read_at이 null이면 미열람
 */
export function isInboxUnread(readAt: string | null): boolean {
  return readAt === null
}

// ============================================================================
// 제안 상태 판정 (프리랜서)
// ============================================================================

/**
 * 제안 응답 대기 여부 판정 (며칠 이상 미응답)
 * - 제출 후 3일 이상 지났고 아직 응답이 없으면 응답 대기 (MVP 기준)
 */
export function isProposalStale(submittedAt: string, status: string): boolean {
  // 이미 결정된 제안은 제외
  if (status === 'ACCEPTED' || status === 'REJECTED') {
    return false
  }

  const submitted = new Date(submittedAt)
  const now = new Date()
  const diffDays = (now.getTime() - submitted.getTime()) / (1000 * 60 * 60 * 24)
  return diffDays >= 3
}

/**
 * 제안 경과일 계산 (일수)
 */
export function calculateDaysSinceSubmission(submittedAt: string): number {
  const submitted = new Date(submittedAt)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - submitted.getTime()) / (1000 * 60 * 60 * 24))
  return diffDays
}

// ============================================================================
// 프로젝트 CTA 라벨 결정
// ============================================================================

/**
 * 프로젝트 상태별 CTA 라벨 및 링크 결정
 */
export function getProjectCTA(status: ProjectStatusCategory, projectId: number) {
  switch (status) {
    case 'requesting':
      return {
        label: '요청 수정',
        href: `/client/projects/${projectId}?edit=true`,
      }
    case 'reviewing':
      return {
        label: '견적 비교',
        href: `/client/projects/${projectId}/quotes`,
      }
    case 'in_progress':
      return {
        label: '대시보드',
        href: `/client/projects/${projectId}/dashboard`,
      }
  }
}
